import type { Dispatch, SetStateAction } from "react";
import { use } from "react";
import { startViewTransition } from "use-start-view-transition";

import { EditorContextProvider } from "@/components/app/editor-v3/editor-context";
import { PostEditor } from "@/components/app/editor-v3/editors/post-editor";
import { toPostRequestParams } from "@/components/app/editor-v3/transforms/to-post-request-params";
import { Skeleton } from "@/components/ui/skeleton";
import { useFeedContext } from "@/contexts/feed-context";
import { UserContext } from "@/contexts/user-context";
import { useSubmitPost } from "@/hooks/use-submit-post";

import type { PendingPost } from "../../feed-pending-post-item";
import { PendingPostStatus } from "../../feed-pending-post-item";

export const FeedMainHeader = ({
  setPendingPosts,
}: {
  setPendingPosts: Dispatch<SetStateAction<PendingPost[]>>;
}) => {
  const { onPostCreated } = useFeedContext();
  const { user } = use(UserContext);
  const submitPost = useSubmitPost();

  return (
    <div className="p-4">
      <EditorContextProvider
        enableFileDrop
        onSubmit={async (editorContext) => {
          const setOpen = editorContext.exposed?.setOpen;

          if (typeof setOpen === "function") {
            setOpen(false);
          }

          const postRequestParams = await toPostRequestParams({
            editorContext,
            user: user!,
          });
          const pendingPostId = crypto.randomUUID();

          editorContext.reset();

          const removePendingPost = () => {
            setPendingPosts?.((prev) =>
              prev.filter((p) => p.id !== pendingPostId),
            );
          };

          startViewTransition(() => {
            setPendingPosts((prev) => [
              {
                body: editorContext.body || editorContext.gatedBody,
                id: pendingPostId,
                status: PendingPostStatus.Pending,
                onRetry: () => {
                  editorContext.setEditorContext(editorContext);
                },
                onCancel: removePendingPost,
              },
              ...prev,
            ]);
          });

          try {
            const post = await submitPost(postRequestParams, {
              toleratePostHydrationFailure: true,
            });

            await startViewTransition(() => {
              removePendingPost();
              if (post) {
                onPostCreated?.(post);
              }
            });
          } catch {
            setPendingPosts?.((prev) =>
              prev.map((p) => {
                if (p.id === pendingPostId) {
                  p.status = PendingPostStatus.Error;
                }
                return p;
              }),
            );
          }
        }}
      >
        <PostEditor autoOverlayEditor />
      </EditorContextProvider>
    </div>
  );
};

export const FeedMainHeaderSkeleton = () => {
  return (
    <div
      className="
        flex h-[66px] items-center gap-3 p-4
        md:h-[65px]
      "
    >
      <Skeleton
        className="
          hidden size-8 shrink-0 rounded-full
          md:block
        "
      />
      <Skeleton
        className="
          h-4 w-[155px] max-w-[45%] rounded-sm
          md:w-[170px]
        "
      />
      <div className="ml-auto flex items-center gap-4">
        <Skeleton className="size-4 rounded-sm" />
        <Skeleton className="size-4 rounded-sm" />
        <Skeleton className="size-4 rounded-sm" />
        <Skeleton
          className="
            hidden size-4 rounded-sm
            md:block
          "
        />
      </div>
    </div>
  );
};
